import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"
import LinksFieldset from "../../links/pages/LinksFieldset"
import type { LinkRow, LinksFieldsetLabels } from "../../links/pages/LinksFieldset"

// t018 `impl-forms` (FR-31, FR-39): the seven `links.*` widget-UI keys
// join the stay form's own label bundle, plus `linksMapHint` -- the
// widget's own second hint, read only here (the item form never passes
// it). `optional` (below) is reused, not duplicated under a `links*`
// name, the same as the item form.
interface AdminStayFormLabels {
  nameLabel: string
  nameHint: string
  addressLabel: string
  addressHint: string
  checkInLabel: string
  checkInDateAria: string
  checkInTimeAria: string
  checkOutLabel: string
  checkOutDateAria: string
  checkOutTimeAria: string
  checkOutHint: string
  noteLabel: string
  noteHint: string
  optional: string
  linksHeading: string
  linksRowHeading: string
  linksRemove: string
  linksAdd: string
  linksLabelLabel: string
  linksUrlLabel: string
  linksHint: string
  linksMapHint: string
  save: string
  delete: string
  deleteConfirm: string
}

interface AdminStayFormProps {
  heading: string
  backHref: string
  backLabel: string
  // The collection route for a new stay (POST) or the stay's own address
  // (PATCH) -- distinct from `backHref`, the `AdminContactForm` shape.
  submitHref: string
  // `null` on the new-stay form -- absent, not merely hidden, is what
  // keeps «Удалить жильё» off that screen and tells this component which
  // HTTP verb `submitHref` expects.
  deleteHref: string | null
  stay: {
    // `null` on the new-stay form; the four boundary fields arrive
    // machine-format (ISO date, zero-padded 24-hour time) -- exactly
    // what `<input type="date">`/`<input type="time">` require, never
    // routed through I18n.
    name: string | null
    address: string | null
    checkInOn: string | null
    checkInAtTime: string | null
    checkOutOn: string | null
    checkOutAtTime: string | null
    notes: string | null
    // t018 `impl-forms` (FR-19, FR-20): the stay's own links, in
    // `position` order -- `[]` on the new-stay form.
    links: LinkRow[]
  }
  labels: AdminStayFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute names (the `AdminContactForm` shape): the server's
// failed-save `errors` prop round-trips under these same bare keys, and
// the wire payload nests them under "stay" only at submit time (via
// `transform`). t018 `impl-forms` (FR-20): `links` joins this same flat
// state, the same reasoning `AdminScheduleItemForm`'s own `ItemFormData`
// carries.
interface StayFormData {
  name: string
  address: string
  check_in_on: string
  check_in_at_time: string
  check_out_on: string
  check_out_at_time: string
  notes: string
  links: LinkRow[]
  authenticity_token: string
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const BOUNDARY_DATE_CLASSES = `${INPUT_CLASSES} grow tabular-nums`
const BOUNDARY_TIME_CLASSES = `${INPUT_CLASSES} w-26 tabular-nums`
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const OPTIONAL_LABEL_CLASSES = "flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The stay form (`Stays::AdminStaysController#new/#edit`,
// docs/design/stays.html's Block 4 verbatim): create and edit share this
// one component, the heading and the delete button's presence being the
// only differences (both server-computed) -- the landed
// `AdminContactForm` shape. No client-side format check runs before
// submit -- the server is the single source of validity, and every
// field's value is handed to `useForm` and back to the server untouched.
export default function AdminStayForm({
  heading, backHref, backLabel, submitHref, deleteHref, stay, labels,
}: AdminStayFormProps) {
  const form = useForm<StayFormData>({
    name: stay.name ?? "",
    address: stay.address ?? "",
    check_in_on: stay.checkInOn ?? "",
    check_in_at_time: stay.checkInAtTime ?? "",
    check_out_on: stay.checkOutOn ?? "",
    check_out_at_time: stay.checkOutAtTime ?? "",
    notes: stay.notes ?? "",
    links: stay.links,
    authenticity_token: csrfToken(),
  })

  form.transform((data) => ({
    stay: {
      name: data.name, address: data.address, check_in_on: data.check_in_on,
      check_in_at_time: data.check_in_at_time, check_out_on: data.check_out_on,
      check_out_at_time: data.check_out_at_time, notes: data.notes, links: data.links,
    },
    authenticity_token: data.authenticity_token,
  }))

  // t018 `impl-forms`: the widget's own labels, built once from this
  // form's flat bundle -- `optional` is this form's own key, reused
  // verbatim; `extraHint` (below, at the mount site) is this host's own
  // second hint, read only here.
  const linksLabels: LinksFieldsetLabels = {
    heading: labels.linksHeading,
    optional: labels.optional,
    rowHeading: labels.linksRowHeading,
    remove: labels.linksRemove,
    add: labels.linksAdd,
    labelLabel: labels.linksLabelLabel,
    urlLabel: labels.linksUrlLabel,
    hint: labels.linksHint,
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (deleteHref === null) {
      form.post(submitHref)
    } else {
      form.patch(submitHref)
    }
  }

  function handleDelete(href: string) {
    if (window.confirm(labels.deleteConfirm)) {
      form.delete(href)
    }
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading}</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.nameLabel}</span>
          <input
            type="text"
            value={form.data.name}
            onChange={(event) => form.setData("name", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.nameHint}</span>
          {form.errors.name && <span className={ERROR_CLASSES}>{form.errors.name}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.addressLabel}</span>
          <input
            type="text"
            value={form.data.address}
            onChange={(event) => form.setData("address", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.addressHint}</span>
          {form.errors.address && <span className={ERROR_CLASSES}>{form.errors.address}</span>}
        </label>
        {/* Заезд: a date input paired with a time input, side by side --
            two schema columns, not a datetime. The visible caption names
            the pair; each input carries its own aria-label (FR-24's
            "labelled"). */}
        <div className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.checkInLabel}</span>
          <div className="flex gap-2">
            <input
              type="date"
              aria-label={labels.checkInDateAria}
              value={form.data.check_in_on}
              onChange={(event) => form.setData("check_in_on", event.target.value)}
              className={BOUNDARY_DATE_CLASSES}
            />
            <input
              type="time"
              aria-label={labels.checkInTimeAria}
              value={form.data.check_in_at_time}
              onChange={(event) => form.setData("check_in_at_time", event.target.value)}
              className={BOUNDARY_TIME_CLASSES}
            />
          </div>
          {form.errors.check_in_on && (
            <span className={ERROR_CLASSES}>{form.errors.check_in_on}</span>
          )}
          {form.errors.check_in_at_time && (
            <span className={ERROR_CLASSES}>{form.errors.check_in_at_time}</span>
          )}
        </div>
        {/* Выезд: the same shape as Заезд above. FR-4's one business rule
            (check-out not before check-in) attaches its error to
            check_out_on server-side, so a failed save's message renders
            here. */}
        <div className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.checkOutLabel}</span>
          <div className="flex gap-2">
            <input
              type="date"
              aria-label={labels.checkOutDateAria}
              value={form.data.check_out_on}
              onChange={(event) => form.setData("check_out_on", event.target.value)}
              className={BOUNDARY_DATE_CLASSES}
            />
            <input
              type="time"
              aria-label={labels.checkOutTimeAria}
              value={form.data.check_out_at_time}
              onChange={(event) => form.setData("check_out_at_time", event.target.value)}
              className={BOUNDARY_TIME_CLASSES}
            />
          </div>
          <span className={HINT_CLASSES}>{labels.checkOutHint}</span>
          {form.errors.check_out_on && (
            <span className={ERROR_CLASSES}>{form.errors.check_out_on}</span>
          )}
          {form.errors.check_out_at_time && (
            <span className={ERROR_CLASSES}>{form.errors.check_out_at_time}</span>
          )}
        </div>
        {/* t018 `impl-forms` (FR-31): after «Выезд», before «Заметка» --
            carrying the stay form's own extra hint, since «Карта» is
            derived from the address, never a stored link row. */}
        <LinksFieldset
          rows={form.data.links}
          onChange={(links) => form.setData("links", links)}
          labels={linksLabels}
          extraHint={labels.linksMapHint}
          errors={form.errors as Record<string, string | undefined>}
        />
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.noteLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <textarea
            value={form.data.notes}
            onChange={(event) => form.setData("notes", event.target.value)}
            className="min-h-20 rounded-[10px] border border-gray-300 px-3 py-2.5 text-[15px] leading-[22px] text-gray-900"
          />
          <span className={HINT_CLASSES}>{labels.noteHint}</span>
        </label>
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
        {deleteHref !== null && (
          <button
            type="button"
            onClick={() => handleDelete(deleteHref)}
            className="flex items-center justify-center gap-1.5 py-1 text-sm font-medium text-red-700"
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1.5 1.5 0 001.5 1.4h3.6A1.5 1.5 0 0011.3 13l.7-9M6.7 7v4.5M9.3 7v4.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {labels.delete}
          </button>
        )}
      </form>
    </AdminLayout>
  )
}
